import React, { useEffect, useId, useRef, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { mapPreviewUrl } from '../../utils/shootSummary';
import ClockTimeField from './ClockTimeField';
import './PreferredDateField.css';
import './ShootLocationField.css';

export interface ShootLocation {
  name: string;
  address: string;
  mapsUrl: string;
}

export const EMPTY_SHOOT_LOCATION: ShootLocation = { name: '', address: '', mapsUrl: '' };

export function hasShootLocation(value?: Partial<ShootLocation> | null): boolean {
  return Boolean(value && (value.name?.trim() || value.address?.trim() || value.mapsUrl?.trim()));
}

export function normalizeShootLocation(value?: Partial<ShootLocation> | null): ShootLocation {
  return {
    name: String(value?.name || ''),
    address: String(value?.address || ''),
    mapsUrl: String(value?.mapsUrl || ''),
  };
}

/** A pin the client can open: the saved Google Maps link, or a Maps search for the address. */
export function shootMapLink(value?: Partial<ShootLocation> | null): string {
  if (!value) return '';
  if (value.mapsUrl) return value.mapsUrl;
  const query = [value.name, value.address].filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
}

const GOOGLE_MAPS = /^https:\/\/(maps\.app\.goo\.gl|maps\.google\.com|goo\.gl\/maps|([a-z0-9-]+\.)*google\.[a-z.]+\/maps)/i;

export function isGoogleMapsLink(value: string): boolean {
  return !value.trim() || GOOGLE_MAPS.test(value.trim());
}

interface PlaceSearchProps {
  onSelect: (place: ShootLocation) => void;
}

/**
 * Search OpenStreetMap for a venue or address. Choosing a result fills the
 * place name, the address, and a Google Maps pin.
 */
export const PlaceSearch = ({ onSelect }: PlaceSearchProps) => {
  const id = useId();
  const listId = `${id}-places`;
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ShootLocation[]>([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      setActive(-1);
      setStatus(q ? 'Type a little more of the place name.' : '');
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setStatus('Searching…');
      try {
        const response = await fetch(`${API_BASE_URL}/places?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        const body = await response.json().catch(() => ({}));
        if (controller.signal.aborted) return;
        if (!response.ok) {
          setResults([]);
          setOpen(false);
          setStatus(typeof body.error === 'string' ? body.error : 'Place search is unavailable. Type the address below.');
          return;
        }
        const places = Array.isArray(body.places)
          ? body.places.filter((place: Partial<ShootLocation>) => place && (place.name || place.address)).map(normalizeShootLocation)
          : [];
        setResults(places);
        setOpen(places.length > 0);
        setActive(places.length ? 0 : -1);
        setStatus(places.length ? `${places.length} ${places.length === 1 ? 'place' : 'places'}. Choose one.` : 'No places matched. Type the address below.');
      } catch {
        if (controller.signal.aborted) return;
        setResults([]);
        setOpen(false);
        setStatus('Place search is unavailable. Type the address below.');
      }
    }, 300);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const choose = (place: ShootLocation) => {
    onSelect(place);
    setQuery('');
    setResults([]);
    setOpen(false);
    setActive(-1);
    setStatus(`${place.name || 'Place'} selected. Check the details below.`);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && results.length) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index + 1) % results.length);
      return;
    }
    if (event.key === 'ArrowUp' && results.length) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index <= 0 ? results.length - 1 : index - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && results[active]) choose(results[active]);
      return;
    }
    if (event.key === 'Escape') setOpen(false);
  };

  return (
    <div className="place-search" ref={rootRef}>
      <label htmlFor={`${id}-search`}>
        <span>Search for a place</span>
        <input
          id={`${id}-search`}
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
          value={query}
          placeholder="Venue, street, or suburb"
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          onKeyDown={onKeyDown}
        />
      </label>
      {open ? (
        <ul id={listId} className="place-search-results" role="listbox">
          {results.map((place, index) => (
            <li key={`${place.mapsUrl}-${index}`} role="presentation">
              <button
                type="button"
                role="option"
                id={`${listId}-${index}`}
                aria-selected={index === active}
                className={index === active ? 'is-active' : ''}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(place)}
              >
                <span className="place-search-name">{place.name || place.address}</span>
                {place.address ? <span className="place-search-address">{place.address}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="place-search-status" role="status">{status}</p>
    </div>
  );
};

/** A small map of a place chosen from search. A pasted link without a pin shows nothing. */
export const ShootMap = ({ mapsUrl, label }: { mapsUrl?: string; label?: string }) => {
  const src = mapPreviewUrl(mapsUrl);
  if (!src) return null;
  const name = label?.trim() || 'the selected place';
  return (
    <iframe
      className="shoot-map"
      title={`Map of ${name}`}
      src={src}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  );
};

interface ShootLocationFieldProps {
  value: ShootLocation;
  onChange: (value: ShootLocation) => void;
  label?: string;
  placeLabel?: string;
  laterLabel?: string;
  className?: string;
  /** Short line shown under the fields. */
  help?: string;
  /** Tighter controls for multi-day session rows; hides the map preview. */
  density?: 'default' | 'compact';
  /** Open the place fields even when nothing is saved yet. */
  openPlaceFields?: boolean;
}

/**
 * Where the shoot happens: a place name, an address and a Google Maps link, or
 * "Decide later". Used by the quotation builder and by the client on their quotation.
 */
const ShootLocationField = ({
  value,
  onChange,
  label = 'Where would you like your shoot?',
  placeLabel = 'I have a place in mind',
  laterLabel = 'Decide later',
  className = '',
  help,
  density = 'default',
  openPlaceFields = false,
}: ShootLocationFieldProps) => {
  const id = useId();
  const [hasPlace, setHasPlace] = useState(openPlaceFields || hasShootLocation(value));
  const compact = density === 'compact';

  // A value arriving from outside (opening a saved draft) switches the choice to match it.
  useEffect(() => {
    if (hasShootLocation(value)) setHasPlace(true);
  }, [value]);

  const set = (patch: Partial<ShootLocation>) => onChange({ ...normalizeShootLocation(value), ...patch });
  const choose = (next: boolean) => {
    setHasPlace(next);
    if (!next) onChange({ ...EMPTY_SHOOT_LOCATION });
  };
  const badLink = !isGoogleMapsLink(value.mapsUrl || '');

  return (
    <fieldset className={`preferred-date shoot-location ${compact ? 'preferred-date-compact shoot-location-compact' : ''} ${className}`.trim()}>
      <legend>{label}</legend>
      <div className="preferred-date-choice" role="radiogroup" aria-label={label}>
        <button type="button" role="radio" aria-checked={hasPlace} className={hasPlace ? 'is-selected' : ''} onClick={() => choose(true)}>
          {placeLabel}
        </button>
        <button type="button" role="radio" aria-checked={!hasPlace} className={!hasPlace ? 'is-selected' : ''} onClick={() => choose(false)}>
          {laterLabel}
        </button>
      </div>
      {hasPlace ? (
        <div className="shoot-location-fields">
          <PlaceSearch onSelect={onChange} />
          {compact ? null : <ShootMap mapsUrl={value.mapsUrl} label={value.name || value.address} />}
          <label htmlFor={`${id}-name`}>
            <span>Place name</span>
            <input id={`${id}-name`} type="text" maxLength={200} value={value.name} onChange={(event) => set({ name: event.target.value })} placeholder="Kirstenbosch Gardens" autoComplete="off" />
          </label>
          <label htmlFor={`${id}-address`}>
            <span>Address</span>
            <input id={`${id}-address`} type="text" maxLength={400} value={value.address} onChange={(event) => set({ address: event.target.value })} placeholder="Rhodes Drive, Newlands, Cape Town" autoComplete="off" />
          </label>
          <label htmlFor={`${id}-maps`}>
            <span>Google Maps link (optional)</span>
            <input
              id={`${id}-maps`}
              type="url"
              inputMode="url"
              maxLength={500}
              value={value.mapsUrl}
              onChange={(event) => set({ mapsUrl: event.target.value })}
              placeholder="https://maps.app.goo.gl/…"
              aria-invalid={badLink}
              autoComplete="off"
            />
          </label>
          {badLink ? <p className="shoot-location-error" role="alert">Please paste a Google Maps link, or leave this empty.</p> : null}
        </div>
      ) : null}
      {help ? <p className="shoot-location-help">{help}</p> : null}
    </fieldset>
  );
};

interface PreferredTimeFieldProps {
  /** HH:MM, or '' when there is no preference. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
}

/** An optional preferred start time for the shoot. */
export const PreferredTimeField = ({ value, onChange, label = 'Preferred start time (optional)', className = '' }: PreferredTimeFieldProps) => {
  const id = useId();
  return (
    <div className={`shoot-time ${className}`.trim()}>
      <label htmlFor={id}>{label}</label>
      <ClockTimeField id={id} ariaLabel={label} value={value} onChange={onChange} />
      {value ? <button type="button" className="shoot-time-clear" onClick={() => onChange('')}>Clear</button> : null}
    </div>
  );
};

export default ShootLocationField;
